import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import { useBuySellStore } from '@/reducers/transactionReducer'
import {
  MAX_9999_SELF_SERVE_AMOUNT,
  PaymentType,
  PaymentTypes,
} from '@/utils/currencies'

const OPTIONS: {
  value: PaymentType
  titleKey: string
  descriptionKey: string
  limitKey: string
}[] = [
  {
    value: PaymentTypes.REQUEST,
    titleKey: 'paymentMethod.interac.title',
    descriptionKey: 'paymentMethod.interac.description',
    limitKey: 'paymentMethod.interac.limit',
  },
  {
    value: PaymentTypes.WIRE,
    titleKey: 'paymentMethod.wire.title',
    descriptionKey: 'paymentMethod.wire.description',
    limitKey: 'paymentMethod.wire.limit',
  },
]

type PaymentMethodSelectorProps = {
  maxSelfServeCad: number
}

export const PaymentMethodSelector = ({
  maxSelfServeCad,
}: PaymentMethodSelectorProps) => {
  const { t, i18n } = useTranslation()
  const requestType = useBuySellStore.use.requestType()
  const setRequestType = useBuySellStore.use.setRequestType()

  const formatCad = (amount: number): string =>
    amount.toLocaleString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA')

  const limitFor = (value: PaymentType): number =>
    value === PaymentTypes.WIRE ? MAX_9999_SELF_SERVE_AMOUNT : maxSelfServeCad

  return (
    <div className="flex flex-col gap-1" data-testid="container-payment-method">
      <div
        className="text-xs uppercase"
        data-testid="text-payment-method-label"
      >
        {t('paymentMethod.label')}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {OPTIONS.map((option) => {
          const selected = requestType === option.value
          return (
            <button
              type="button"
              key={option.value}
              data-testid={`btn-payment-method-${option.value}`}
              aria-pressed={selected}
              onClick={() => setRequestType(option.value)}
              className={clsx(
                'flex flex-col rounded border-2 px-3 py-2 text-left leading-tight transition-colors',
                selected
                  ? 'border-clementine dark:border-sun'
                  : 'border-rock hover:border-pebble'
              )}
            >
              <span className="text-sm font-medium">{t(option.titleKey)}</span>
              <span className="text-xs text-pebble dark:text-rock">
                {t(option.descriptionKey)}
              </span>
              <span className="mt-1 text-xs font-semibold">
                {t(option.limitKey, {
                  amount: formatCad(limitFor(option.value)),
                })}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
